/**
 * Podcast oynatma arayüzü (2026-10-22): penceredeki kontrol çubuğu (PodcastControls) ve uygulamada gezinirken ekranda
 * yüzen mini oynatıcı (PodcastMiniPlayer, App kökünde). İkisi de services/podcastPlayer.ts'teki tek oynatıcıyı yönetir.
 * Mini oynatıcı, podcast penceresi oynatıcı görünümünü göstermiyorken görünür; başlığa basınca pencere açılır
 * (MainApp `kpss-open-podcast` olayını dinler; ders seçim ekranındaysa yalnız kontroller çalışır).
 */
import React, { useEffect, useState } from 'react';
import * as podcastPlayer from '../services/podcastPlayer';
import { usePodcastPlayer } from '../utils/usePodcastPlayer';

const fmt = (t: number) => {
    if (!Number.isFinite(t) || t < 0) t = 0;
    const m = Math.floor(t / 60), s = Math.floor(t % 60);
    return `${m}:${s < 10 ? '0' : ''}${s}`;
};
const RATES = [1, 1.25, 1.5, 1.75, 2, 0.75];

/** Penceredeki kontrol çubuğu: oynat/duraklat, 15 sn geri/ileri, ilerleme, hız */
export const PodcastControls: React.FC = () => {
    const ps = usePodcastPlayer();
    const [drag, setDrag] = useState<number | null>(null);
    const pos = drag ?? ps.currentTime;
    const multi = ps.queueLength > 1 || ps.generating;
    return (
        <div className="flex flex-col gap-1.5 px-2 py-2">
            <div className="flex items-center gap-2">
                <button onClick={() => podcastPlayer.skip(-15)} aria-label="15 saniye geri" className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-700 font-bold text-[11px] active:scale-95">↺15</button>
                <button onClick={() => podcastPlayer.toggle()} aria-label={ps.playing ? 'Duraklat' : 'Oynat'} disabled={!ps.queueLength}
                    className="w-11 h-11 rounded-full bg-indigo-600 text-white text-lg shadow active:scale-95 disabled:opacity-50 flex items-center justify-center">
                    {ps.waiting ? <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> : ps.playing ? '❚❚' : '▶'}
                </button>
                <button onClick={() => podcastPlayer.skip(15)} aria-label="15 saniye ileri" className="w-9 h-9 rounded-full bg-indigo-50 text-indigo-700 font-bold text-[11px] active:scale-95">15↻</button>
                <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="text-[11px] font-mono text-gray-500 tabular-nums">{fmt(pos)}</span>
                    <input type="range" min={0} max={Math.max(1, ps.duration)} step={0.5} value={Math.min(pos, ps.duration || 0)}
                        onChange={e => setDrag(Number(e.target.value))}
                        onPointerUp={() => { if (drag !== null) { podcastPlayer.seek(drag); setDrag(null); } }}
                        onKeyUp={() => { if (drag !== null) { podcastPlayer.seek(drag); setDrag(null); } }}
                        className="flex-1 min-w-0 accent-indigo-600" aria-label="Konum" />
                    <span className="text-[11px] font-mono text-gray-500 tabular-nums">{fmt(ps.duration)}</span>
                </div>
                <button onClick={() => podcastPlayer.setRate(RATES[(RATES.indexOf(ps.rate) + 1) % RATES.length])} aria-label="Hız"
                    className="px-2 h-8 rounded-full bg-gray-100 text-gray-700 text-[11px] font-bold tabular-nums">{ps.rate}x</button>
            </div>
            {multi && <p className="text-[10px] text-gray-400 text-center">Bölüm {ps.index + 1}/{ps.queueLength}{ps.generating ? ' · sonraki bölümler hazırlanıyor…' : ''}</p>}
        </div>
    );
};

/** Uygulamada gezinirken yüzen mini oynatıcı */
const PodcastMiniPlayer: React.FC = () => {
    const ps = usePodcastPlayer();
    const [expanded, setExpanded] = useState(true);
    useEffect(() => { if (ps.session) setExpanded(true); }, [ps.session?.key]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!ps.session || ps.modalOpen) return null;
    const progress = ps.duration > 0 ? Math.min(1, ps.currentTime / ps.duration) : 0;
    // Pencere açıksa (liste görünümünde) oynatıcı görünümüne döner; kapalıysa açar
    const open = () => {
        window.dispatchEvent(new CustomEvent('kpss-open-podcast', { detail: { subject: ps.session?.subject } }));
        window.dispatchEvent(new Event('kpss-podcast-show'));
    };

    if (!expanded) {
        return (
            <button onClick={() => setExpanded(true)} aria-label="Podcast oynatıcıyı aç"
                className="fixed z-[10000] right-3 w-12 h-12 rounded-full bg-gradient-to-br from-violet-600 to-indigo-700 text-white shadow-xl flex items-center justify-center active:scale-95"
                style={{ bottom: 'calc(var(--sab) + 84px)' }}>
                <span className={`text-xl ${ps.playing ? 'animate-pulse' : ''}`}>🎧</span>
                <svg className="absolute inset-0 -rotate-90" viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="22" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="3" strokeDasharray={`${progress * 138.2} 138.2`} /></svg>
            </button>
        );
    }

    return (
        <div className="fixed z-[10000] left-2 right-2 sm:left-auto sm:right-3 sm:w-[380px] rounded-2xl bg-white/95 border border-indigo-200 shadow-2xl overflow-hidden"
            style={{ bottom: 'calc(var(--sab) + 84px)' }}>
            <div className="h-1 bg-indigo-100"><div className="h-full bg-indigo-600" style={{ width: `${progress * 100}%` }} /></div>
            <div className="flex items-center gap-2 px-2.5 py-2">
                <button onClick={open} className="flex items-center gap-2 flex-1 min-w-0 text-left" aria-label="Podcast penceresini aç">
                    <span className="w-9 h-9 shrink-0 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-700 text-white flex items-center justify-center">
                        <span className={ps.playing ? 'animate-pulse' : ''}>🎧</span>
                    </span>
                    <span className="min-w-0">
                        <span className="block text-[13px] font-bold text-gray-900 truncate">{ps.session.title}</span>
                        <span className="block text-[11px] text-gray-500 truncate">{ps.waiting ? 'Sonraki bölüm hazırlanıyor…' : `${fmt(ps.currentTime)} / ${fmt(ps.duration)}${ps.session.subtitle ? ' · ' + ps.session.subtitle : ''}`}</span>
                    </span>
                </button>
                <button onClick={() => podcastPlayer.skip(-15)} aria-label="15 saniye geri" className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-700 font-bold text-[10px]">↺15</button>
                <button onClick={() => podcastPlayer.toggle()} aria-label={ps.playing ? 'Duraklat' : 'Oynat'} className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center">
                    {ps.waiting ? <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" /> : ps.playing ? '❚❚' : '▶'}
                </button>
                <button onClick={() => podcastPlayer.skip(15)} aria-label="15 saniye ileri" className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-700 font-bold text-[10px]">15↻</button>
                <div className="flex flex-col gap-1">
                    <button onClick={() => setExpanded(false)} aria-label="Küçült" className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-[11px]">▾</button>
                    <button onClick={() => podcastPlayer.close()} aria-label="Podcast'i kapat" className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-[11px]">✕</button>
                </div>
            </div>
        </div>
    );
};

export default PodcastMiniPlayer;
